import orderBy from 'lodash/orderBy'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { convertDate, dateToString } from 'modules/core/lib/dates'

import { usePreview } from 'modules/shift_management/hooks/usePreview'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type { Shift, ShiftsRow } from 'modules/shift_management/lib/types'

interface Props {
  shifts: Shift[]
  shiftsRow: ShiftsRow
  date: moment.Moment
}

export const useShiftsToDisplay = ({ shiftsRow, shifts, date }: Props) => {
  const [{ view }] = useSchedule()
  const [{ selectedDaysByRow, shifts: previewShifts, plannedBreaks }] = usePreview()
  const hasPreviewShifts = !!previewShifts.length
  // Convert to date without time to check if the selected date has preview
  const selectedDates = useUnsafeMemo(
    () =>
      (selectedDaysByRow[shiftsRow.key] || []).map((selectedDate: string) =>
        convertDate(selectedDate, view.dateFormat, 'backend')
      ),
    [selectedDaysByRow, view.dateFormat, shiftsRow.id]
  )
  const isSelectedDay = useUnsafeMemo(
    () => selectedDates.includes(dateToString(date, 'backend')),
    [date, selectedDates]
  )
  const showPreview = isSelectedDay && hasPreviewShifts

  const shiftsWithPreview = useUnsafeMemo(
    () => (showPreview ? shifts.concat(previewShifts) : shifts),
    [shifts, showPreview, previewShifts]
  )

  const shiftsToDisplay = useUnsafeMemo(
    () => orderBy(shiftsWithPreview, ['startTime', 'endTime'], ['asc', 'asc']),
    [shiftsWithPreview]
  )

  return {
    shiftsToDisplay,
    previewPlannedBreaks: plannedBreaks,
  }
}
